import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  startTransition,
} from "react";
import { Link, useParams } from "react-router-dom";
import Select from "react-select";
import { enqueueSnackbar } from "notistack";
import DropDownStyles from "../../../components/content/dropdownStyles";
import {
  SAMPLING_THEME,
  createSamplingFilterSelectStyles,
  samplingFilterLabelRowStyle,
  samplingFilterLabelStyle,
  samplingFetchButtonStyle,
  samplingCheckboxLabelStyle,
  samplingCheckboxTextStyle,
  samplingCheckboxInputStyle,
} from "../samplingFilterTheme";
import Preloader from "../../../components/custom/preLoader";
import PondDetailWorkflowTabs from "../../../components/custom/PondDetailWorkflowTabs";
import PondDetailWorkflowPageHeader from "../../../components/custom/PondDetailWorkflowPageHeader";
import axiosInstance, {
  checkTokenValidity,
  CheckUserExpiry,
  clearAuthData,
  getUserDetails,
  isFisherWideUser,
  isJwtAuthFailure,
  isSuperUser,
} from "../../../components/content/axiosConfig";
import { fetchPondDropdownOptions, resolveFisherId } from "../../../utils/superPanelApi";
import { useQueryClient } from "@tanstack/react-query";
import {
  enrichPondDetailsCached,
  normalizeFishTypesKey,
} from "../../../hooks/queries/useSamplingEnrichment";
import "react-datepicker/dist/react-datepicker.css";
import CustomDateTimePicker from "../../../components/custom/dateTimePicker";
import showErrorToast from "../../../components/toast/toast";
import { SamplingGraphExportProvider } from "./SamplingGraphExportContext";
import { generateSamplingGraphPdf } from "./generateSamplingGraphPdf";
import BinsTable from "./BinsTable";
import { graphTypes } from "./graphTypeOptions";
import {
  GRAPH_TYPES_POND_DETAIL,
  WQ_GRAPH_TYPE_IDS,
  graphTypeNeedsBinSize,
  graphTypeNeedsUnitSystem,
  isPondDetailGraphType,
} from "../pondStat/wqChartConfigs";
import { PsdTableSupplement, BinTableSupplement } from "../graphViewV2/PondStatGraphSupplements";
import {
  buildPsdVsDateRows,
  compactPondDetailsForPsd,
} from "./psdMetrics";
import {
  applyTagModeToGraphRows,
  buildGraphDataFromPondDetails,
} from "./data/processGraphData";
import {
  buildStockingPredatorGraphRows,
  filterStockingEventsByGraphRange,
} from "./data/stockingPredatorGraphRows";
import { formatStockedSpeciesSubtitle } from "./formatStockedSpeciesSubtitle";
import { FormatWrText } from "../../../utils/samplingDisplayLabels";
import { MAX_INDIVIDUAL_TAGS } from "./constants/tagLimits";
import { buildTagBatches } from "./constants/tagBatchUtils";
import { formatDate, formatDateTime } from "./formatters";
import { processBinData } from "./processBinData";
import GraphChart from "./GraphChart";
import {
  Boxes,
  CalendarRange,
  Download,
  Fish,
  Hash,
  Layers,
  LineChart,
  Loader2,
  Play,
  Ruler,
  Tags,
  Waves,
} from "lucide-react";

/** WQ charts only need session metadata + water-quality columns — drop fish arrays. */
const toLightWqSessions = (details) =>
  (details || []).map((d) => ({
    ...d,
    SamplingData: [],
    FishType: [],
    PondNotes: [],
  }));

/** Graphs that always use all fish; tag mode / tag pickers are hidden. */
const GRAPH_TYPES_NO_TAG_CONTROLS = new Set([
  "avg-wr-vs-date",
  "standard-vs-actual",
  "weight-vs-length-by-date",
  "number-frequency",
  "number-frequency-vs-bin",
  "number-frequency-vs-bin-by-date",
  ...GRAPH_TYPES_POND_DETAIL,
]);

const FishDataGraphView = ({ GraphChartComponent = GraphChart }) => {
  const { pondDetailId } = useParams();
  const isSinglePondDetailMode = Boolean(pondDetailId);
  const userDetails = getUserDetails();
  const superUser = isSuperUser(userDetails);
  const FisherID = userDetails.FisherID;
  const fisherWide = isFisherWideUser(userDetails);
  const [selectedUnit, setSelectedUnit] = useState({
    label: "English System",
    value: "English",
  });

  const [selectedPond, setSelectedPond] = useState(null);
  const [selectedFish, setSelectedFish] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [useAllData, setUseAllData] = useState(true);
  const [selectedGraphType, setSelectedGraphType] = useState("");
  const [pendingGraphType, setPendingGraphType] = useState(null);
  const [graphData, setGraphData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [pondDetails, setPondDetails] = useState([]);
  const [ponds, setPonds] = useState([]);
  const [fishTypes, setFishTypes] = useState([]);
  const [selectedPondInfo, setSelectedPondInfo] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const graphRef = useRef(null);
  const plotExportRef = useRef(null);
  // Track hovered scatter point for weight-vs-length so tooltip only shows on exact dots
  const [hoveredWeightPoint, setHoveredWeightPoint] = useState(null);
  const [selectedBinSize, setSelectedBinSize] = useState(null);
  const [selectedTagMode, setSelectedTagMode] = useState({
    label: "All Fish",
    value: "all",
  });
  const [selectedTags, setSelectedTags] = useState([]);
  const [includeUntagged, setIncludeUntagged] = useState(false);

  useEffect(() => {
    if (selectedTagMode?.value === "grouped") {
      setSelectedTagMode({ label: "All Fish", value: "all" });
      setIncludeUntagged(false);
      setGraphData([]);
    }
  }, [selectedTagMode?.value]);
  const [preloadedTagRows, setPreloadedTagRows] = useState([]);
  const [isTagOptionsLoading, setIsTagOptionsLoading] = useState(false);
  const [hasTagOptionsLoaded, setHasTagOptionsLoaded] = useState(false);
  /** True when DistinctTags finished with zero tags or hard failure. */
  const [tagOptionsLoadFailed, setTagOptionsLoadFailed] = useState(false);
  const tagLoadInFlightRef = useRef(false);
  const hasTagOptionsLoadedRef = useRef(false);
  /** Bumped to cancel in-flight tag / session enrich work (avoids lockups). */
  const tagLoadGenRef = useRef(0);
  const sessionLoadGenRef = useRef(0);
  const queryClient = useQueryClient();
  const [singlePondDetailRow, setSinglePondDetailRow] = useState(null);
  const [includeStockingPredators, setIncludeStockingPredators] = useState(false);
  const [stockingEventsForGraph, setStockingEventsForGraph] = useState([]);
  const stockingEventsRef = useRef([]);
  const [allFishTypesCatalog, setAllFishTypesCatalog] = useState([]);
  const [focusDetailId, setFocusDetailId] = useState(null);
  /** Metadata-only sessions for population-pct-cumulative-vs-bin (no SamplingData until pick). */
  const [sessionList, setSessionList] = useState([]);
  const [isSessionEnrichLoading, setIsSessionEnrichLoading] = useState(false);
  /** Precomputed PSD vs date rows (avoids re-walking fish when expanding the table). */
  const [psdVsDateRows, setPsdVsDateRows] = useState(null);

  const syncStockingEventsForGraph = useCallback((events) => {
    const list = Array.isArray(events) ? events : [];
    const prev = stockingEventsRef.current;
    if (
      prev.length === list.length &&
      (list.length === 0 || prev.every((item, i) => item === list[i]))
    ) {
      return;
    }
    stockingEventsRef.current = list;
    setStockingEventsForGraph(list);
  }, []);

  /** Drop enriched SamplingData / chart rows so switching graphs cannot pile up memory. */
  const clearHeavyGraphPayloads = useCallback(() => {
    tagLoadGenRef.current += 1;
    sessionLoadGenRef.current += 1;
    tagLoadInFlightRef.current = false;
    setIsTagOptionsLoading(false);
    setIsSessionEnrichLoading(false);

    setPondDetails([]);
    setSessionList([]);
    setFocusDetailId(null);
    setGraphData([]);
    setPsdVsDateRows(null);
    setHoveredWeightPoint(null);
    syncStockingEventsForGraph([]);
    queryClient.removeQueries({ queryKey: ["samplingEnrich"] });
  }, [queryClient, syncStockingEventsForGraph]);

  const fishTypeLookupOptions = useMemo(() => {
    const byId = new Map();
    for (const o of fishTypes || []) {
      if (o?.value != null) byId.set(String(o.value), o);
    }
    for (const ft of allFishTypesCatalog || []) {
      const id = ft?.id;
      if (id == null) continue;
      const key = String(id);
      if (byId.has(key)) continue;
      const label = ft.Name ?? ft.FishTypeName ?? ft.name;
      if (label) byId.set(key, { value: id, label: String(label) });
    }
    return [...byId.values()];
  }, [fishTypes, allFishTypesCatalog]);

  // Bin size options based on unit system
  const getBinSizeOptions = () => {
    if (selectedUnit.value === "English") {
      return [
        { label: "1 Inch", value: 1 },
        { label: "2 Inch", value: 2 },
      ];
    }
    return [
      { label: "10 mm", value: 10 },
      { label: "20 mm", value: 20 },
      { label: "40 mm", value: 40 },
    ];
  };

  // Set default bin size when unit system changes
  useEffect(() => {
    if (selectedUnit.value === "English") {
      setSelectedBinSize({ label: "1 Inch", value: 1 });
    } else {
      setSelectedBinSize({ label: "10 mm", value: 10 });
    }
  }, [selectedUnit]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get("/api/FishTypes/GetAllFishTypes");
        if (!cancelled) setAllFishTypesCatalog(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to load fish types catalog for graph subtitles", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filterSelectStyles = useMemo(
    () => createSamplingFilterSelectStyles(DropDownStyles),
    []
  );

  const FetchPonds = useCallback(async () => {
    if (!superUser && !FisherID) return;

    setIsLoading(true);
    try {
      const dropdownPonds = await fetchPondDropdownOptions(
        axiosInstance,
        userDetails,
        fisherWide
      );
      setPonds(dropdownPonds);
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        showErrorToast("Session expired. Please log in again.");
      } else {
        const errorMessage =
          error.response?.data?.message || "Failed to fetch ponds.";
        console.error("Failed to fetch ponds", error);
        showErrorToast(errorMessage);
        setPonds([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [FisherID, fisherWide, superUser]);

  const FetchFishTypes = useCallback(async (pondId) => {
    if (!pondId) return;
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(
        `/api/PondFishes/GetAllPondFishes/${pondId}`,
        {
          params: { search: "" },
        }
      );
      const dropdownFishTypes =
        response.data?.map((fish) => ({
          label: fish.FishTypeName,
          value: fish.FishTypeID,
        })) || [];
      setFishTypes(dropdownFishTypes);
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        showErrorToast("Session expired. Please log in again.");
      } else {
        const errorMessage =
          error.response?.data?.message || "Failed to fetch fish types.";
        console.error("Failed to fetch fish types", error);
        showErrorToast(errorMessage);
        setFishTypes([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const FetchSelectedPond = useCallback(
    async (pondId) => {
      if (!pondId) return;
      try {
        setIsLoading(true);
        const pondResponse = await axiosInstance.get(`/api/Pond/GetPondByID`, {
          params: { id: pondId },
        });
        const pond = pondResponse.data;
        if (pond) {
          setSelectedPondInfo({
            id: pond.id,
            Name: pond.Name || "N/A",
            Location: pond.Location || "N/A",
            Forage: pond.Forage || "N/A",
            ForageType: pond.ForageType || "N/A",
            Type: pond.Type || "N/A",
            Age: pond.Age ? pond.Age.toString() : "N/A",
            Size: pond.Size ? pond.Size.toString() : "N/A",
            Depth: pond.Depth ? pond.Depth.toString() : "N/A",
            AquaticVegetationPresent: pond.AquaticVegetationPresent || "N/A",
            WaterSupply: pond.WaterSupply || "N/A",
            FisherID,
          });
        }
        await FetchFishTypes(pondId);
      } catch (error) {
        if (isJwtAuthFailure(error)) {
          clearAuthData();
          window.location.href = "/user/login";
          showErrorToast("Session expired. Please log in again.");
        } else {
          const errorMessage =
            error.response?.data?.message || "Failed to fetch pond details.";
          console.error("Failed to fetch pond details", error);
          showErrorToast(errorMessage);
          setSelectedPondInfo(null);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [FetchFishTypes, FisherID]
  );

  const initSinglePondDetail = useCallback(async () => {
    if (!pondDetailId) return;
    if (!superUser && !FisherID) return;
    try {
      setIsLoading(true);
      const pdRes = superUser
        ? await axiosInstance.get(`/api/PondDetail/GetPondDetailByID`, {
            params: { id: pondDetailId },
          })
        : await axiosInstance.get(`/api/PondDetail/GetPondDetail`, {
            params: { id: pondDetailId, FisherID },
          });
      const pd = pdRes.data;
      if (!pd) {
        showErrorToast("Pond detail not found");
        setSinglePondDetailRow(null);
        return;
      }
      const effectiveFisherId = superUser ? pd.FisherID : FisherID;
      setSinglePondDetailRow({ ...pd, id: pd.id });
      const pondRes = await axiosInstance.get(`/api/Pond/GetPondByID`, {
        params: { id: pd.PondID },
      });
      const pond = pondRes.data || {};
      const pondLabel = pond.Name || `Pond ${pd.PondID}`;
      setSelectedPond({ value: pd.PondID, label: pondLabel });
      setSelectedPondInfo({
        id: pond.id,
        Name: pond.Name || "N/A",
        Location: pond.Location || "N/A",
        Forage: pond.Forage || "N/A",
        ForageType: pond.ForageType || "N/A",
        Type: pond.Type || "N/A",
        Age: pond.Age ? pond.Age.toString() : "N/A",
        Size: pond.Size ? pond.Size.toString() : "N/A",
        Depth: pond.Depth ? pond.Depth.toString() : "N/A",
        AquaticVegetationPresent: pond.AquaticVegetationPresent || "N/A",
        WaterSupply: pond.WaterSupply || "N/A",
        FisherID: effectiveFisherId,
      });
      const fishRes = await axiosInstance.get(
        `/api/SamplingFishes/GetAllFishesBYPondDID/${pondDetailId}`
      );
      setFishTypes(
        (fishRes.data || []).map((f) => ({
          label: f.FishTypeName,
          value: f.FishTypeID,
        }))
      );
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        showErrorToast("Session expired. Please log in again.");
      } else {
        const errorMessage =
          error.response?.data?.message || "Failed to load sampling session.";
        showErrorToast(errorMessage);
        setSinglePondDetailRow(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [pondDetailId, FisherID, superUser]);

  const fetchStockingEventsForGraph = useCallback(
    async (pondId) => {
      const stockingFisherId = resolveFisherId(userDetails, selectedPondInfo?.FisherID);
      const stockingParams = stockingFisherId ? { FisherID: stockingFisherId } : {};
      const res = await axiosInstance.get(`/api/PondStocking/GetByPond/${pondId}`, {
        params: stockingParams,
      });
      const events = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      if (isSinglePondDetailMode) {
        const sessionYmd = singlePondDetailRow?.Date
          ? formatDate(singlePondDetailRow.Date)
          : null;
        if (!sessionYmd) return [];
        return filterStockingEventsByGraphRange({
          events,
          useAllData: false,
          startYmd: null,
          endYmd: null,
          singleSessionYmd: sessionYmd,
        });
      }
      const startYmd = !useAllData && startDate ? formatDate(startDate) : null;
      const endYmd = !useAllData && endDate ? formatDate(endDate) : null;
      return filterStockingEventsByGraphRange({
        events,
        useAllData,
        startYmd,
        endYmd,
        singleSessionYmd: null,
      });
    },
    [
      FisherID,
      isSinglePondDetailMode,
      selectedPondInfo?.FisherID,
      singlePondDetailRow,
      useAllData,
      startDate,
      endDate,
      userDetails,
    ]
  );

  useEffect(() => {
    if (!includeStockingPredators) {
      syncStockingEventsForGraph([]);
      return;
    }
    const pondId = isSinglePondDetailMode
      ? singlePondDetailRow?.PondID
      : selectedPond?.value;
    if (!pondId) return;

    let cancelled = false;
    (async () => {
      try {
        const events = await fetchStockingEventsForGraph(pondId);
        if (!cancelled) syncStockingEventsForGraph(events);
      } catch (err) {
        console.error("Failed to prefetch stocking events for graph", err);
        if (!cancelled) syncStockingEventsForGraph([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    includeStockingPredators,
    selectedPond?.value,
    singlePondDetailRow?.PondID,
    isSinglePondDetailMode,
    fetchStockingEventsForGraph,
    syncStockingEventsForGraph,
    useAllData,
    startDate,
    endDate,
  ]);

  const fetchAdditionalPondData = useCallback(
    async (detail) => {
      try {
        const [enriched] = await enrichPondDetailsCached(
          queryClient,
          [detail],
          {
            pondId: detail?.PondID ?? selectedPond?.value ?? null,
            startDate: useAllData ? null : formatDate(startDate),
            endDate: useAllData ? null : endDate ? formatDate(endDate) : null,
            useAllData,
            fishTypes: selectedFish,
          }
        );
        return (
          enriched || {
            ...detail,
            SamplingData: [],
            FishType: [],
          }
        );
      } catch (error) {
        console.error("Error fetching additional pond data:", error);
        return { ...detail, SamplingData: [], FishType: [] };
      }
    },
    [endDate, queryClient, selectedFish, selectedPond?.value, startDate, useAllData]
  );

  const enrichDetailsBulk = useCallback(
    async (details) => {
      try {
        return await enrichPondDetailsCached(queryClient, details, {
          pondId: selectedPond?.value ?? details?.[0]?.PondID ?? null,
          startDate: useAllData ? null : formatDate(startDate),
          endDate: useAllData ? null : endDate ? formatDate(endDate) : null,
          useAllData,
          fishTypes: selectedFish,
        });
      } catch (error) {
        console.error("Error fetching additional pond data:", error);
        return (details || []).map((detail) => ({
          ...detail,
          SamplingData: [],
          FishType: [],
        }));
      }
    },
    [endDate, queryClient, selectedFish, selectedPond?.value, startDate, useAllData]
  );

  const loadSessionForPopulationBinChart = useCallback(
    async (detailOrId) => {
      const detail =
        detailOrId != null && typeof detailOrId === "object"
          ? detailOrId
          : (sessionList || []).find((d) => Number(d.id) === Number(detailOrId));
      if (!detail?.id) return;

      const loadGen = ++sessionLoadGenRef.current;
      setIsSessionEnrichLoading(true);
      setFocusDetailId(detail.id ?? null);
      try {
        const enriched = await Promise.race([
          fetchAdditionalPondData(detail),
          new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error("Timed out loading sampling session data.")),
              45000
            )
          ),
        ]);
        if (loadGen !== sessionLoadGenRef.current) return;
        setPondDetails([enriched]);
        setFocusDetailId(enriched.id ?? detail.id ?? null);
        setGraphData([]);
      } catch (error) {
        if (loadGen !== sessionLoadGenRef.current) return;
        console.error("Error loading sampling session for bin chart:", error);
        showErrorToast(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to load sampling session data."
        );
        setPondDetails([]);
        setFocusDetailId(null);
      } finally {
        if (loadGen === sessionLoadGenRef.current) {
          setIsSessionEnrichLoading(false);
        }
      }
    },
    [fetchAdditionalPondData, sessionList]
  );

  const validateIndividualTagSelectionForGenerate = useCallback(() => {
    if (GRAPH_TYPES_NO_TAG_CONTROLS.has(pendingGraphType?.value)) return true;
    if (selectedTagMode?.value !== "individual") return true;
    if (selectedTags.length === 0) {
      enqueueSnackbar("Select at least one tag (required for Individual Tags mode).", {
        variant: "warning",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 4000,
      });
      return false;
    }
    if (selectedTags.length > MAX_INDIVIDUAL_TAGS) {
      enqueueSnackbar(`Select at most ${MAX_INDIVIDUAL_TAGS} tags.`, {
        variant: "warning",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 4000,
      });
      return false;
    }
    return true;
  }, [pendingGraphType?.value, selectedTagMode?.value, selectedTags]);

  const handleFetchPondSamplingData = async () => {
    if (isLoading) return;

    if (isSinglePondDetailMode) {
      if (!singlePondDetailRow?.id) {
        enqueueSnackbar("Sampling session is still loading. Try again.", {
          variant: "warning",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          autoHideDuration: 3000,
        });
        return;
      }
      if (!pendingGraphType?.value) {
        enqueueSnackbar("Please select a graph type", {
          variant: "warning",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          autoHideDuration: 3000,
        });
        return;
      }
      if (!validateIndividualTagSelectionForGenerate()) {
        return;
      }
      try {
        setIsLoading(true);
        clearHeavyGraphPayloads();
        setSelectedGraphType(pendingGraphType.value);

        if (WQ_GRAPH_TYPE_IDS.has(pendingGraphType.value)) {
          const light = toLightWqSessions([singlePondDetailRow]);
          startTransition(() => {
            setPondDetails(light);
            setFocusDetailId(light[0]?.id ?? null);
            setGraphData([]);
          });
          return;
        }

        const enriched = await fetchAdditionalPondData(singlePondDetailRow);
        let stockingFiltered = [];
        if (includeStockingPredators && singlePondDetailRow?.PondID) {
          try {
            stockingFiltered = await fetchStockingEventsForGraph(
              singlePondDetailRow.PondID
            );
          } catch (stockErr) {
            console.error("Failed to fetch stocking events for graph", stockErr);
            stockingFiltered = [];
          }
        }
        syncStockingEventsForGraph(stockingFiltered);

        if (pendingGraphType.value === "psd-vs-date") {
          const compact = compactPondDetailsForPsd([enriched]);
          const rows = buildPsdVsDateRows(compact, selectedFish, selectedUnit?.value);
          queryClient.removeQueries({ queryKey: ["samplingEnrich"] });
          startTransition(() => {
            setPsdVsDateRows(rows);
            setPondDetails(compact);
            setSessionList([]);
            setFocusDetailId(enriched.id ?? null);
            setGraphData([]);
          });
          return;
        }

        startTransition(() => {
          setPondDetails([enriched]);
          setSessionList([]);
          setFocusDetailId(enriched.id ?? null);
        });
      } catch (error) {
        if (isJwtAuthFailure(error)) {
          clearAuthData();
          window.location.href = "/user/login";
          showErrorToast("Session expired. Please log in again.");
        } else {
          const errorMessage =
            error.response?.data?.message || "Failed to fetch pond details.";
          console.error("Failed to fetch pond details", error);
          showErrorToast(errorMessage);
          clearHeavyGraphPayloads();
        }
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (!selectedPond?.value) {
      enqueueSnackbar("Please select a pond", {
        variant: "warning",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 3000,
      });
      return;
    }
    if (!useAllData && !startDate) {
      enqueueSnackbar("Please select a start date or choose 'ALL Data'", {
        variant: "warning",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 3000,
      });
      return;
    }
    if (!pendingGraphType?.value) {
      enqueueSnackbar("Please select a graph type", {
        variant: "warning",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 3000,
      });
      return;
    }
    if (!validateIndividualTagSelectionForGenerate()) {
      return;
    }
    try {
      setIsLoading(true);
      clearHeavyGraphPayloads();
      setSelectedGraphType(pendingGraphType.value);
      const effectiveFisherId = resolveFisherId(userDetails, selectedPond?.fisherId);
      const params = {
        PondID: selectedPond.value,
      };
      if (effectiveFisherId) {
        params.FisherID = effectiveFisherId;
      }

      if (!useAllData) {
        params.StartDate = formatDate(startDate);
        if (endDate) {
          params.EndDate = formatDate(endDate);
        }
      }
      const response = await axiosInstance.get(
        "/api/PondDetail/GetPondDetails",
        { params }
      );
      const detailRows = Array.isArray(response.data) ? response.data : [];
      if (!detailRows.length) {
        const emptyMessage =
          response?.data?.message ||
          "No pond details found for the selected criteria";
        enqueueSnackbar(emptyMessage, {
          variant: "info",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          autoHideDuration: 3000,
        });
        clearHeavyGraphPayloads();
        return;
      }

      if (pendingGraphType.value === "population-pct-cumulative-vs-bin") {
        setIsSessionEnrichLoading(false);
        setSessionList(detailRows);
        setPondDetails([]);
        setGraphData([]);
        syncStockingEventsForGraph([]);
        if (detailRows.length === 1) {
          await loadSessionForPopulationBinChart(detailRows[0]);
        } else {
          setFocusDetailId(null);
          const latest = [...detailRows].sort(
            (a, b) => new Date(b.Date) - new Date(a.Date)
          )[0];
          if (latest?.id) {
            await loadSessionForPopulationBinChart(latest);
          }
        }
        return;
      }

      if (WQ_GRAPH_TYPE_IDS.has(pendingGraphType.value)) {
        const light = toLightWqSessions(detailRows);
        const sorted = [...light].sort(
          (a, b) => new Date(a.Date) - new Date(b.Date)
        );
        startTransition(() => {
          setSessionList([]);
          setPondDetails(light);
          setFocusDetailId(sorted[sorted.length - 1]?.id ?? null);
          setGraphData([]);
        });
        return;
      }

      setSessionList([]);
      const pondDetailsWithData = await enrichDetailsBulk(detailRows);
      let stockingFiltered = [];
      if (includeStockingPredators && selectedPond?.value) {
        try {
          stockingFiltered = await fetchStockingEventsForGraph(selectedPond.value);
        } catch (stockErr) {
          console.error("Failed to fetch stocking events for graph", stockErr);
          stockingFiltered = [];
        }
      }
      syncStockingEventsForGraph(stockingFiltered);

      if (pendingGraphType.value === "psd-vs-date") {
        const compact = compactPondDetailsForPsd(pondDetailsWithData);
        const rows = buildPsdVsDateRows(compact, selectedFish, selectedUnit?.value);
        queryClient.removeQueries({ queryKey: ["samplingEnrich"] });
        const sortedPsd = [...compact].sort(
          (a, b) => new Date(a.Date) - new Date(b.Date)
        );
        startTransition(() => {
          setPsdVsDateRows(rows);
          setPondDetails(compact);
          setFocusDetailId(sortedPsd[sortedPsd.length - 1]?.id ?? null);
          setGraphData([]);
        });
        return;
      }

      const sorted = [...pondDetailsWithData].sort(
        (a, b) => new Date(a.Date) - new Date(b.Date)
      );
      startTransition(() => {
        setPondDetails(pondDetailsWithData);
        setFocusDetailId(sorted[sorted.length - 1]?.id ?? null);
      });
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        showErrorToast("Session expired. Please log in again.");
      } else {
        const errorMessage =
          error.response?.data?.message || "Failed to fetch pond details.";
        console.error("Failed to fetch pond details", error);
        showErrorToast(errorMessage);
        clearHeavyGraphPayloads();
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to format date range for graph titles
  const formatDateRange = () => {
    if (isSinglePondDetailMode && singlePondDetailRow?.Date) {
      try {
        const dateObj = new Date(singlePondDetailRow.Date);
        if (!Number.isNaN(dateObj.getTime())) {
          return `${dateObj.getMonth() + 1}/${dateObj.getDate()}/${dateObj.getFullYear()}`;
        }
      } catch {
        /* ignore */
      }
    }

    const formatDateForDisplay = (date) => {
      if (!date) return "";
      const dateObj = new Date(date);
      if (Number.isNaN(dateObj.getTime())) return "";
      return `${dateObj.getMonth() + 1}/${dateObj.getDate()}/${dateObj.getFullYear()}`;
    };

    const spanFromDates = (dates) => {
      const times = (dates || [])
        .map((d) => new Date(d).getTime())
        .filter((t) => Number.isFinite(t));
      if (!times.length) return null;
      const min = formatDateForDisplay(new Date(Math.min(...times)));
      const max = formatDateForDisplay(new Date(Math.max(...times)));
      if (!min) return null;
      if (max && min !== max) return `${min} to ${max}`;
      return min;
    };

    if (useAllData) {
      const fromSessions = spanFromDates(
        (pondDetails.length ? pondDetails : sessionList).map((d) => d?.Date)
      );
      const fromGraph = spanFromDates(graphData.map((d) => d?.date || d?.Date));
      const span = fromSessions || fromGraph;
      return span ? `All Data (${span})` : "All Data";
    }
    if (!startDate) return "";

    const startDateFormatted = formatDateForDisplay(startDate);
    const endDateFormatted = endDate ? formatDateForDisplay(endDate) : "";

    if (endDateFormatted && startDateFormatted !== endDateFormatted) {
      return `${startDateFormatted} to ${endDateFormatted}`;
    } else {
      return startDateFormatted;
    }
  };

  // Helper to format forage for subtitle (comma-separated -> readable)
  const formatForageForSubtitle = (forageString) => {
    if (!forageString || forageString === "N/A") return "";
    const items = forageString.split(",").map((s) => s.trim()).filter(Boolean);
    return items.join(", ");
  };

  // Helper function to get graph title with pond and date range
  const getGraphTitle = () => {
    const graphTypeName = graphTypes.find((t) => t.id === selectedGraphType)?.name || "Fish Data Graph";
    const pondName = selectedPond?.label || "Unknown Pond";
    const dateRange = formatDateRange();

    const isBinGraph =
      selectedGraphType === "number-frequency" ||
      selectedGraphType === "number-frequency-vs-bin" ||
      selectedGraphType === "number-frequency-vs-bin-by-date";
    let subtitle = null;
    if (isBinGraph && graphData.length > 0) {
      const speciesList = [...new Set(graphData.map((d) => d.species))];
      const speciesLabel = speciesList.length === 1 ? speciesList[0] : speciesList.length > 1 ? "All" : "—";
      subtitle = `Sample Size ${graphData.length} ${speciesLabel}`;
      const forageStr = formatForageForSubtitle(selectedPondInfo?.Forage);
      const forageTypeStr = selectedPondInfo?.ForageType && selectedPondInfo.ForageType !== "N/A" ? selectedPondInfo.ForageType : "";
      if (forageStr) subtitle += ` · Forage: ${forageStr}`;
      if (forageTypeStr) subtitle += ` · Additional Forage: ${forageTypeStr}`;
    }
    if (selectedGraphType === "tagged-wr-vs-date") {
      const nTags =
        graphData.length > 0
          ? new Set(graphData.map((d) => d.tagNumber).filter(Boolean)).size
          : 0;
      const tagLine =
        nTags > 0
          ? `${nTags} tagged fish · individual WR per recapture`
          : "Tagged fish only · individual WR per recapture";
      subtitle = subtitle ? `${subtitle} · ${tagLine}` : tagLine;
    } else if (
      !GRAPH_TYPES_NO_TAG_CONTROLS.has(selectedGraphType) &&
      selectedTagMode?.value === "individual" &&
      selectedTags.length > 0
    ) {
      subtitle = subtitle
        ? `${subtitle} · Tagged: ${selectedTags.length} selected`
        : `Tagged: ${selectedTags.length} selected`;
    }

    const stockingEventsForTitle =
      stockingEventsRef.current?.length > 0
        ? stockingEventsRef.current
        : stockingEventsForGraph;

    const stockedSubtitle = includeStockingPredators
      ? formatStockedSpeciesSubtitle({
          graphData,
          stockingEvents: stockingEventsForTitle,
          selectedFish,
          fishTypeOptions: fishTypeLookupOptions,
          fishTypesCatalog: allFishTypesCatalog,
        })
      : null;

    return {
      mainTitle: graphTypeName,
      pondName: pondName,
      dateRange: dateRange,
      subtitle: subtitle,
      stockedSubtitle,
    };
  };

  const handlePondChange = async (selectedOption) => {
    if (isSinglePondDetailMode || isLoading) return;
    setSelectedPond(selectedOption);
    setSelectedFish([]);
    setFishTypes([]);
    setSelectedPondInfo(null);
    setPondDetails([]);
    setSessionList([]);
    setFocusDetailId(null);
    setGraphData([]);
    setUseAllData(true);
    setStartDate(null);
    setEndDate(null);
    setPreloadedTagRows([]);
    setHasTagOptionsLoaded(false);
    hasTagOptionsLoadedRef.current = false;
    setTagOptionsLoadFailed(false);
    setIncludeStockingPredators(false);
    syncStockingEventsForGraph([]);
    if (selectedOption?.value) {
      await FetchSelectedPond(selectedOption.value);
    }
  };

  const handleFishChange = (selectedOption) => {
    setSelectedFish(selectedOption || []);
  };

  const tagOptions = useMemo(() => {
    const sourceRows = [
      ...pondDetails.flatMap((detail) => detail?.SamplingData || []),
      ...preloadedTagRows,
    ];

    const selectedFishLabels = new Set((selectedFish || []).map((f) => f.label));
    const hasSpeciesFilter = selectedFishLabels.size > 0;
    const tagsSet = new Set();

    sourceRows.forEach((fish) => {
      const fishName = (fish?.FishTypeName || "").toString().trim();
      if (hasSpeciesFilter && !selectedFishLabels.has(fishName)) return;

      const normalizedTag = (fish?.TagNumber || "").toString().trim();
      if (!normalizedTag) return;
      tagsSet.add(normalizedTag);
    });

    return Array.from(tagsSet)
      .sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
      )
      .map((tag) => ({ label: tag, value: tag }));
  }, [pondDetails, preloadedTagRows, selectedFish]);

  const tagBatches = useMemo(() => buildTagBatches(tagOptions), [tagOptions]);
  const allowAllIndividualTagsForGraph =
    pendingGraphType?.value === "number-frequency-vs-bin";
  const forceFirstBatchForTaggedGraph =
    pendingGraphType?.value === "tagged-wr-vs-date";

  const showIndividualTagsCheckbox =
    Boolean(pendingGraphType?.value) &&
    !GRAPH_TYPES_NO_TAG_CONTROLS.has(pendingGraphType.value) &&
    !forceFirstBatchForTaggedGraph;

  const individualTagsOnly = selectedTagMode?.value === "individual";

  useEffect(() => {
    if (!forceFirstBatchForTaggedGraph || tagBatches.length === 0) return;
    const firstBatch = tagBatches[0]?.options || [];
    if (firstBatch.length === 0) return;
    if (selectedTagMode?.value !== "individual") {
      setSelectedTagMode({ label: "Individual Tags", value: "individual" });
      setSelectedTags(firstBatch);
      setGraphData([]);
      return;
    }
    // Only auto-fill the first batch when nothing is selected yet.
    if (!selectedTags || selectedTags.length === 0) {
      setSelectedTags(firstBatch);
      setGraphData([]);
    }
  }, [
    forceFirstBatchForTaggedGraph,
    tagBatches,
    selectedTagMode?.value,
    selectedTags,
  ]);

  const quickBatchSelectOptions = useMemo(
    () =>
      tagBatches.map((b, idx) => ({
        label:
          tagBatches.length === 1
            ? `All ${b.options.length} tag${b.options.length === 1 ? "" : "s"} (${b.rangeLabel})`
            : `Batch ${idx + 1}: ${b.rangeLabel} · ${b.options.length} tag${
                b.options.length === 1 ? "" : "s"
              }`,
        value: idx,
      })),
    [tagBatches]
  );

  const selectedFishKey = useMemo(
    () => (selectedFish || []).map((f) => f.value).join(","),
    [selectedFish]
  );

  // Reset tag picker cache only when pond / date range / species filter changes.
  useEffect(() => {
    setPreloadedTagRows([]);
    setHasTagOptionsLoaded(false);
    hasTagOptionsLoadedRef.current = false;
    setTagOptionsLoadFailed(false);
    tagLoadInFlightRef.current = false;
  }, [
    endDate,
    isSinglePondDetailMode,
    pondDetailId,
    selectedFishKey,
    selectedPond?.value,
    selectedPondInfo?.id,
    startDate,
    useAllData,
  ]);

  const loadTagOptionsOnDemand = useCallback(async (force = false) => {
    if (tagLoadInFlightRef.current) return;
    if (!force && hasTagOptionsLoadedRef.current) return;
    const needsTags =
      selectedTagMode?.value === "individual" ||
      pendingGraphType?.value === "tagged-wr-vs-date";
    if (!needsTags) return;

    const memoryTagRows = [];
    const seenMemoryTags = new Set();
    pondDetails.forEach((d) => {
      (d?.SamplingData || []).forEach((f) => {
        const tag = (f?.TagNumber || "").toString().trim();
        if (!tag || seenMemoryTags.has(tag)) return;
        seenMemoryTags.add(tag);
        memoryTagRows.push({
          TagNumber: tag,
          FishTypeName: (f?.FishTypeName || "").toString().trim() || "Unknown",
        });
      });
    });
    if (memoryTagRows.length > 0) {
      setPreloadedTagRows(memoryTagRows);
      hasTagOptionsLoadedRef.current = true;
      setHasTagOptionsLoaded(true);
      setTagOptionsLoadFailed(false);
      return;
    }

    const pondId = isSinglePondDetailMode
      ? singlePondDetailRow?.PondID ?? selectedPondInfo?.id ?? selectedPond?.value
      : selectedPond?.value;
    if (!pondId) return;

    const loadGen = ++tagLoadGenRef.current;
    tagLoadInFlightRef.current = true;
    setIsTagOptionsLoading(true);
    setTagOptionsLoadFailed(false);
    try {
      const effectiveFisherId = resolveFisherId(
        userDetails,
        selectedPond?.fisherId ?? selectedPondInfo?.FisherID
      );
      const params = {
        PondID: pondId,
      };
      if (effectiveFisherId) {
        params.FisherID = effectiveFisherId;
      }
      if (isSinglePondDetailMode && pondDetailId) {
        params.PondDID = pondDetailId;
      } else if (!useAllData && startDate) {
        params.StartDate = formatDate(startDate);
        if (endDate) params.EndDate = formatDate(endDate);
      }
      const fishTypesKey = normalizeFishTypesKey(selectedFish);
      if (fishTypesKey) params.fishTypes = fishTypesKey;

      const response = await axiosInstance.get("/api/PondDetail/DistinctTags", {
        params,
      });
      if (loadGen !== tagLoadGenRef.current) return;
      const tags = Array.isArray(response?.data?.tags) ? response.data.tags : [];
      setPreloadedTagRows(tags);
      hasTagOptionsLoadedRef.current = true;
      setHasTagOptionsLoaded(true);
      setTagOptionsLoadFailed(tags.length === 0);
    } catch (error) {
      if (loadGen !== tagLoadGenRef.current) return;
      console.error("Error loading tag options:", error);
      setPreloadedTagRows([]);
      hasTagOptionsLoadedRef.current = true;
      setHasTagOptionsLoaded(true);
      setTagOptionsLoadFailed(true);
      const status = error?.response?.status;
      enqueueSnackbar(
        status === 404 || status === 405
          ? "Tag list API is not on this server yet. Deploy DistinctTags or use a local backend."
          : "Could not load tag numbers. Open the dropdown to retry.",
        {
          variant: "error",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          autoHideDuration: 6000,
        }
      );
    } finally {
      if (loadGen === tagLoadGenRef.current) {
        tagLoadInFlightRef.current = false;
        setIsTagOptionsLoading(false);
      }
    }
  }, [
    endDate,
    isSinglePondDetailMode,
    pendingGraphType?.value,
    pondDetailId,
    pondDetails,
    selectedFish,
    selectedPond,
    selectedPondInfo,
    selectedTagMode?.value,
    singlePondDetailRow?.PondID,
    startDate,
    useAllData,
    userDetails,
  ]);

  useEffect(() => {
    if (
      selectedTagMode?.value !== "individual" &&
      pendingGraphType?.value !== "tagged-wr-vs-date"
    ) {
      return;
    }
    loadTagOptionsOnDemand();
  }, [
    selectedTagMode?.value,
    pendingGraphType?.value,
    loadTagOptionsOnDemand,
  ]);

  const CheckExpiry = useCallback(async () => {
    try {
      await CheckUserExpiry();
    } catch (error) {
      console.error("Error checking user expiry:", error);
    }
  }, []);

  useEffect(() => {
    checkTokenValidity();
    CheckExpiry();
  }, [CheckExpiry]);

  useEffect(() => {
    if (!isSinglePondDetailMode) return;
    initSinglePondDetail();
  }, [isSinglePondDetailMode, initSinglePondDetail]);

  useEffect(() => {
    if (isSinglePondDetailMode) return;
    FetchPonds();
  }, [isSinglePondDetailMode, FetchPonds]);

  const downloadFile = async (blob, filename, mimeType) => {
    // Detect if the user is on a mobile device
    const isMobile =
      /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    try {
      if (isMobile) {
        // For mobile devices, upload to server and redirect to file URL
        const formData = new FormData();
        formData.append("file", blob, filename);

        const response = await axiosInstance.post(
          "api/files/upload",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        if (response.data.error) {
          throw new Error(response.data.error);
        }

        const fileUrl = response.data.url;
        window.open(fileUrl, "_blank");
      } else {
        // For desktop devices, trigger direct download
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.style.display = "none";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      }
    } catch (error) {
      console.error("Download failed:", error);

      // Fallback for both mobile and desktop
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result;
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = filename;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        enqueueSnackbar(
          `Download failed. Use this URL to download: ${dataUrl}`,
          {
            variant: "error",
            anchorOrigin: { vertical: "top", horizontal: "right" },
            autoHideDuration: 5000,
          }
        );
      };
      reader.readAsDataURL(blob);
    }
  };

  const generatePDF = async () => {
    if (!graphRef.current) {
      enqueueSnackbar("No graph available to export", {
        variant: "warning",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 3000,
      });
      return;
    }

    try {
      // Do not use isLoading here — it replaces the page with Preloader and unmounts the graph.
      setIsPdfExporting(true);
      await generateSamplingGraphPdf({
        graphRef,
        plotExportRef,
        getGraphTitle,
        downloadFile,
      });

      enqueueSnackbar("PDF generated successfully!", {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 2000,
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      enqueueSnackbar("Failed to generate PDF", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 3000,
      });
    } finally {
      setIsPdfExporting(false);
    }
  };



  const hasPlottableGraph =
    graphData.length > 0 ||
    (isPondDetailGraphType(selectedGraphType) && pondDetails.length > 0);

  const showGraphPanel =
    hasPlottableGraph ||
    (selectedGraphType === "population-pct-cumulative-vs-bin" &&
      sessionList.length > 0);

  const focusDetail = useMemo(
    () =>
      pondDetails.find((d) => Number(d.id) === Number(focusDetailId)) || null,
    [pondDetails, focusDetailId]
  );

  const focusSessionOptions = useMemo(
    () =>
      [...(sessionList.length > 0 ? sessionList : pondDetails)]
        .sort((a, b) => new Date(a.Date) - new Date(b.Date))
        .map((d) => ({ value: d.id, label: formatDateTime(d.Date) })),
    [sessionList, pondDetails]
  );

  const renderGraph = () => (
    <GraphChartComponent
      key={selectedGraphType || "none"}
      selectedGraphType={selectedGraphType}
      graphData={graphData}
      pondDetails={pondDetails}
      selectedUnit={selectedUnit}
      selectedFish={selectedFish}
      isMobile={windowWidth <= 768}
      hoveredWeightPoint={hoveredWeightPoint}
      setHoveredWeightPoint={setHoveredWeightPoint}
      selectedBinSize={selectedBinSize}
      focusDetailId={focusDetailId}
      precomputedPsdRows={psdVsDateRows}
    />
  );

  const processGraphData = useCallback(
    (pondDetailsArg, graphType, stockingOverride) => {
      if (isPondDetailGraphType(graphType)) {
        setGraphData([]);
        return;
      }
      const ctx = {
        selectedUnit,
        selectedFish,
        selectedTagMode,
        selectedTags,
        includeUntagged,
        formatDateTime,
        fishTypeOptions: fishTypeLookupOptions,
        fishTypesCatalog: allFishTypesCatalog,
      };
      const sampling = buildGraphDataFromPondDetails(pondDetailsArg, graphType, ctx);
      const ev =
        stockingOverride !== undefined && stockingOverride !== null
          ? stockingOverride
          : stockingEventsForGraph;
      const stockingRows =
        includeStockingPredators && Array.isArray(ev) && ev.length > 0
          ? buildStockingPredatorGraphRows(ev, graphType, ctx)
          : [];
      let combined = applyTagModeToGraphRows(
        [...sampling, ...stockingRows],
        graphType,
        ctx
      );
      if (graphType === "avg-wr-vs-date" || graphType === "tagged-wr-vs-date") {
        combined.sort((a, b) => new Date(a.date) - new Date(b.date));
      }
      setGraphData(combined);
    },
    [
      selectedUnit,
      selectedFish,
      selectedTagMode,
      selectedTags,
      includeUntagged,
      includeStockingPredators,
      stockingEventsForGraph,
      fishTypeLookupOptions,
      allFishTypesCatalog,
    ]
  );

  useEffect(() => {
    if (selectedGraphType !== "psd-vs-date") return;
    if (!pondDetails?.length) {
      setPsdVsDateRows(null);
      return;
    }
    setPsdVsDateRows(buildPsdVsDateRows(pondDetails, selectedFish, selectedUnit?.value));
  }, [selectedFish, selectedGraphType, pondDetails, selectedUnit?.value]);

  useEffect(() => {
    if (!selectedGraphType || pondDetails.length === 0) return;
    processGraphData(pondDetails, selectedGraphType);
  }, [
    pondDetails,
    selectedGraphType,
    processGraphData,
    selectedUnit,
    selectedFish,
    selectedTagMode,
    selectedTags,
    includeUntagged,
    includeStockingPredators,
    stockingEventsForGraph,
    fishTypeLookupOptions,
    allFishTypesCatalog,
  ]);

  const pageInner = (
    <div className="donezo-table-view">
        {!isSinglePondDetailMode && (
          <div className="d-flex align-items-center mb-3">
            <div>
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Home</Link>
                </li>
                <li className="breadcrumb-item active">Graph View</li>
              </ul>
              <h1 className="page-header mb-0">Graph View</h1>
            </div>
          </div>
        )}
        <div className="donezo-sampling-filter">
          <div className="donezo-sampling-filter__accent" />

          <div className="donezo-sampling-filter__body">
            <div className="tab-content">
              <div className="tab-pane fade show active" id="allTab">

                {/* ── ROW 1 : Pond · Fish · Date Range (pond & date hidden for single PondDetail URL) ── */}
                <div className="row g-2 align-items-end">
                  {!isSinglePondDetailMode && (
                    <div className="col-12 col-md-3">
                      <div style={samplingFilterLabelRowStyle}>
                        <Waves size={13} color={SAMPLING_THEME.iconPond} strokeWidth={2.5} />
                        <span style={samplingFilterLabelStyle}>Pond</span>
                      </div>
                      <Select
                        inputId="graph-filter-pond"
                        onChange={handlePondChange}
                        options={ponds}
                        value={selectedPond}
                        styles={filterSelectStyles}
                        menuPortalTarget={document.body}
                        isMulti={false}
                        placeholder="Select a pond..."
                        isClearable
                      />
                    </div>
                  )}
                  <div className={isSinglePondDetailMode ? "col-12 col-md-6" : "col-12 col-md-3"}>
                    <div style={samplingFilterLabelRowStyle}>
                      <Fish size={13} color={SAMPLING_THEME.iconFish} strokeWidth={2.5} />
                      <span style={samplingFilterLabelStyle}>Fish Species</span>
                    </div>
                    <Select
                      inputId="graph-filter-fish"
                      onChange={handleFishChange}
                      options={fishTypes}
                      value={selectedFish}
                      styles={filterSelectStyles}
                      menuPortalTarget={document.body}
                      isMulti={true}
                      placeholder="Select fish..."
                      isClearable
                    />
                  </div>
                  {!isSinglePondDetailMode && (
                    <div className="col-12 col-md-6">
                      <div style={samplingFilterLabelRowStyle}>
                        <CalendarRange size={13} color={SAMPLING_THEME.iconDate} strokeWidth={2.5} />
                        <span style={samplingFilterLabelStyle}>Date Range</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <label
                          htmlFor="useAllData"
                          style={samplingCheckboxLabelStyle(useAllData)}
                        >
                          <input
                            type="checkbox"
                            id="useAllData"
                            checked={useAllData}
                            onChange={(e) => {
                              setUseAllData(e.target.checked);
                              if (e.target.checked) {
                                setStartDate(null);
                                setEndDate(null);
                              }
                            }}
                            style={samplingCheckboxInputStyle}
                          />
                          <span style={samplingCheckboxTextStyle(useAllData)}>
                            ALL Data
                          </span>
                        </label>
                        {!useAllData && (
                          <>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <CustomDateTimePicker
                                label=""
                                mode="date"
                                value={startDate}
                                onChange={(date) => setStartDate(date)}
                                ampm={true}
                                placeholder="Start date"
                              />
                            </div>
                            <span style={{ color: "#cbd5e1", fontSize: 14, fontWeight: 500, flexShrink: 0 }}>—</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <CustomDateTimePicker
                                label=""
                                mode="date"
                                value={endDate}
                                onChange={(date) => setEndDate(date)}
                                placeholder="End date"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {(selectedPond?.value || isSinglePondDetailMode) && (
                  <div className="row g-2 mt-1">
                    <div className="col-12">
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <label
                          htmlFor="includeStockingPredators"
                          className={`donezo-sampling-filter__chip${includeStockingPredators ? " is-active" : ""}`}
                        >
                          <input
                            type="checkbox"
                            id="includeStockingPredators"
                            checked={includeStockingPredators}
                            onChange={(e) => {
                              setIncludeStockingPredators(e.target.checked);
                              setGraphData([]);
                            }}
                            style={samplingCheckboxInputStyle}
                          />
                          <Boxes size={14} color={SAMPLING_THEME.iconFish} strokeWidth={2.5} />
                          <span className="donezo-sampling-filter__chip-text">
                            Include stocking events data (apply on Generate)
                          </span>
                        </label>
                        {showIndividualTagsCheckbox && (
                          <label
                            htmlFor="individualTagsOnly"
                            className={`donezo-sampling-filter__untagged${individualTagsOnly ? " is-active" : ""}`}
                          >
                            <input
                              type="checkbox"
                              id="individualTagsOnly"
                              checked={individualTagsOnly}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setSelectedTagMode(
                                  checked
                                    ? { label: "Individual Tags", value: "individual" }
                                    : { label: "All Fish", value: "all" }
                                );
                                setSelectedTags([]);
                                setGraphData([]);
                                if (checked) {
                                  setHasTagOptionsLoaded(false);
                                  hasTagOptionsLoadedRef.current = false;
                                  setTagOptionsLoadFailed(false);
                                }
                              }}
                              style={{
                                width: 15,
                                height: 15,
                                accentColor: "#f59e0b",
                                cursor: "pointer",
                              }}
                            />
                            <Tags size={14} color="#f59e0b" strokeWidth={2.5} />
                            <span className="donezo-sampling-filter__untagged-text">
                              Individual tags only
                            </span>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Divider ── */}
                <div className="donezo-sampling-filter__divider" />

                {/* ── ROW 2 : Graph · Units · Actions ── */}
                <div className="row g-2 align-items-end">
                  <div className="col-6 col-md-3">
                    <div className="donezo-sampling-filter__label-row">
                      <LineChart size={13} color={SAMPLING_THEME.iconFish} strokeWidth={2.5} />
                      <span className="donezo-sampling-filter__label">Graph Type</span>
                    </div>
                    <Select
                      onChange={(option) => {
                        setPendingGraphType(option);
                        setSelectedGraphType("");
                        clearHeavyGraphPayloads();
                        if (option?.value === "tagged-wr-vs-date") {
                          setSelectedTagMode({
                            label: "Individual Tags",
                            value: "individual",
                          });
                          setSelectedTags([]);
                          setHasTagOptionsLoaded(false);
                          hasTagOptionsLoadedRef.current = false;
                          setTagOptionsLoadFailed(false);
                        } else if (GRAPH_TYPES_NO_TAG_CONTROLS.has(option?.value)) {
                          setSelectedTagMode({ label: "All Fish", value: "all" });
                          setSelectedTags([]);
                          tagLoadGenRef.current += 1;
                          tagLoadInFlightRef.current = false;
                          setIsTagOptionsLoading(false);
                        }
                      }}
                      options={graphTypes.map((type) => ({
                        value: type.id,
                        label: type.name,
                      }))}
                      value={pendingGraphType}
                      styles={filterSelectStyles}
                      menuPortalTarget={document.body}
                      placeholder="Choose..."
                      isClearable
                    />
                  </div>
                  {graphTypeNeedsUnitSystem(pendingGraphType?.value) && (
                  <div className="col-6 col-md-2">
                    <div style={samplingFilterLabelRowStyle}>
                      <Ruler size={13} color={SAMPLING_THEME.iconNeutral} strokeWidth={2.5} />
                      <span style={samplingFilterLabelStyle}>Units</span>
                    </div>
                    <Select
                      onChange={(option) => setSelectedUnit(option)}
                      options={[
                        { label: "English System", value: "English" },
                        { label: "Metric System", value: "Metric" },
                      ]}
                      styles={filterSelectStyles}
                      menuPortalTarget={document.body}
                      placeholder="Units..."
                      value={selectedUnit}
                    />
                  </div>
                  )}

                  {graphTypeNeedsBinSize(pendingGraphType?.value) && (
                    <div className="col-6 col-md-2">
                      <div style={samplingFilterLabelRowStyle}>
                        <Layers size={13} color={SAMPLING_THEME.iconDate} strokeWidth={2.5} />
                        <span style={samplingFilterLabelStyle}>Bin Size</span>
                      </div>
                      <Select
                        onChange={(option) => setSelectedBinSize(option)}
                        options={getBinSizeOptions()}
                        value={selectedBinSize}
                        styles={filterSelectStyles}
                        menuPortalTarget={document.body}
                        placeholder="Bin..."
                        isClearable={false}
                      />
                    </div>
                  )}

                  {/* Generate / PDF on this row except when Individual Tags (actions move to tag row) */}
                  {selectedTagMode?.value !== "individual" && (
                  <div className="col-12 col-md-3 d-flex align-items-end" style={{ gap: 8 }}>
                    <button
                      type="button"
                      onClick={handleFetchPondSamplingData}
                      disabled={isPdfExporting || isLoading}
                      style={{
                        ...samplingFetchButtonStyle,
                        cursor: isPdfExporting || isLoading ? "not-allowed" : "pointer",
                        opacity: isPdfExporting || isLoading ? 0.65 : 1,
                      }}
                    >
                      <Play size={14} strokeWidth={2.5} />
                      Generate
                    </button>
                    {hasPlottableGraph && (
                      <button
                        type="button"
                        onClick={generatePDF}
                        disabled={isPdfExporting}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 6,
                          height: 36,
                          padding: "0 14px",
                          borderRadius: 8,
                          border: "1.5px solid var(--sf-control-border, #e2e8f0)",
                          background: "var(--sf-control-bg, #fff)",
                          color: "var(--sf-control-text, #475569)",
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: isPdfExporting ? "wait" : "pointer",
                          opacity: isPdfExporting ? 0.65 : 1,
                          transition: "all 0.2s",
                        }}
                      >
                        {isPdfExporting ? (
                          <Loader2
                            size={14}
                            strokeWidth={2.5}
                            style={{ animation: "spin 0.8s linear infinite" }}
                          />
                        ) : (
                          <Download size={14} strokeWidth={2.5} />
                        )}
                        {isPdfExporting ? "Generating PDF…" : "PDF"}
                      </button>
                    )}
                  </div>
                  )}
                </div>

                {/* ── ROW 3 (conditional): Individual tags + quick batches + actions ── */}
                {!GRAPH_TYPES_NO_TAG_CONTROLS.has(pendingGraphType?.value) &&
                  selectedTagMode?.value === "individual" && (
                  <div className="row g-2 mt-1 align-items-end">
                    {!allowAllIndividualTagsForGraph && (
                    <div className="col-12 col-md-3">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          marginBottom: 6,
                        }}
                      >
                        <Layers size={13} color="#22c55e" strokeWidth={2.5} />
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#64748b",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                          }}
                        >
                          Quick batches (≤{MAX_INDIVIDUAL_TAGS} tags)
                        </span>
                      </div>
                      <Select
                        options={quickBatchSelectOptions}
                        value={null}
                        onChange={(opt) => {
                          if (opt == null) return;
                          const batch = tagBatches[opt.value];
                          if (batch?.options?.length) {
                            setSelectedTags(batch.options);
                            setGraphData([]);
                          }
                        }}
                        onMenuOpen={() => {
                          if (
                            tagOptionsLoadFailed ||
                            (hasTagOptionsLoaded && tagBatches.length === 0)
                          ) {
                            loadTagOptionsOnDemand(true);
                          } else {
                            loadTagOptionsOnDemand();
                          }
                        }}
                        styles={filterSelectStyles}
                        menuPortalTarget={document.body}
                        placeholder={
                          isTagOptionsLoading
                            ? "Loading tags…"
                            : tagBatches.length > 0
                              ? tagBatches.length === 1
                                ? `Load all ${tagOptions.length} tag(s) at once…`
                                : forceFirstBatchForTaggedGraph
                                  ? "Select a batch…"
                                  : "Load a batch preset (up to 15 tags)…"
                              : !hasTagOptionsLoaded
                                ? "Waiting for tags…"
                                : tagOptionsLoadFailed
                                  ? "Failed — open to retry…"
                                  : "No tagged fish found…"
                        }
                        isClearable={false}
                        isSearchable={tagBatches.length > 6}
                        isLoading={isTagOptionsLoading}
                        isDisabled={isTagOptionsLoading || tagBatches.length === 0}
                      />
                    </div>
                    )}
                    <div className="col-12 col-md">
                      <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 6 }}>
                        <Hash size={13} color="#f43f5e" strokeWidth={2.5} />
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Tag Numbers</span>
                      </div>
                      <Select
                        isMulti={true}
                        options={tagOptions}
                        value={selectedTags}
                        onChange={(option) => {
                          if (forceFirstBatchForTaggedGraph) return;
                          const next = option || [];
                          if (
                            !allowAllIndividualTagsForGraph &&
                            next.length > MAX_INDIVIDUAL_TAGS
                          ) {
                            enqueueSnackbar(
                              `You can select at most ${MAX_INDIVIDUAL_TAGS} tags.`,
                              {
                                variant: "warning",
                                anchorOrigin: { vertical: "top", horizontal: "right" },
                                autoHideDuration: 4000,
                              }
                            );
                            setSelectedTags(next.slice(0, MAX_INDIVIDUAL_TAGS));
                          } else {
                            setSelectedTags(next);
                          }
                          setGraphData([]);
                        }}
                        onMenuOpen={() => {
                          if (
                            tagOptionsLoadFailed ||
                            (hasTagOptionsLoaded && tagOptions.length === 0)
                          ) {
                            loadTagOptionsOnDemand(true);
                          } else {
                            loadTagOptionsOnDemand();
                          }
                        }}
                        isLoading={isTagOptionsLoading}
                        noOptionsMessage={() =>
                          isTagOptionsLoading
                            ? "Loading tag numbers..."
                            : tagOptionsLoadFailed
                              ? "Failed to load — open menu to retry"
                              : "No tag numbers found for current filters"
                        }
                        styles={filterSelectStyles}
                        menuPortalTarget={document.body}
                        placeholder={
                          allowAllIndividualTagsForGraph
                            ? "Select tag numbers (all allowed)..."
                            : forceFirstBatchForTaggedGraph
                              ? "First batch auto-selected for this graph"
                              : "Select tag numbers..."
                        }
                        closeMenuOnSelect={false}
                        isClearable
                        isDisabled={forceFirstBatchForTaggedGraph}
                      />
                    </div>
                    <div className="col-12 col-md-auto d-flex align-items-end flex-shrink-0" style={{ gap: 8 }}>
                      <button
                        type="button"
                        onClick={handleFetchPondSamplingData}
                        disabled={isPdfExporting || isLoading}
                        style={{
                          ...samplingFetchButtonStyle,
                          flex: "none",
                          whiteSpace: "nowrap",
                          cursor: isPdfExporting || isLoading ? "not-allowed" : "pointer",
                          opacity: isPdfExporting || isLoading ? 0.65 : 1,
                        }}
                      >
                        <Play size={14} strokeWidth={2.5} />
                        Generate
                      </button>
                      {hasPlottableGraph && (
                        <button
                          type="button"
                          onClick={generatePDF}
                          disabled={isPdfExporting}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            height: 36,
                            padding: "0 14px",
                            borderRadius: 8,
                            border: "1.5px solid var(--sf-control-border, #e2e8f0)",
                            background: "var(--sf-control-bg, #fff)",
                            color: "var(--sf-control-text, #475569)",
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: isPdfExporting ? "wait" : "pointer",
                            opacity: isPdfExporting ? 0.65 : 1,
                            transition: "all 0.2s",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {isPdfExporting ? (
                            <Loader2
                            size={14}
                            strokeWidth={2.5}
                            style={{ animation: "spin 0.8s linear infinite" }}
                          />
                          ) : (
                            <Download size={14} strokeWidth={2.5} />
                          )}
                          {isPdfExporting ? "Generating PDF…" : "PDF"}
                        </button>
                      )}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>

        {showGraphPanel && (
          <div className="card border-0 shadow-sm mt-4 p-4 donezo-graph-chart-card">
                <div className="text-center mb-4 pb-3 border-bottom donezo-graph-chart-card__header">
                  {(() => {
                    const titleInfo = getGraphTitle();
                    return (
                      <div>
                        <h2 className="h4 fw-bold mb-2 donezo-graph-chart-card__title">
                          <FormatWrText text={titleInfo.mainTitle} />
                        </h2>
                        <div className="fs-6 fw-semibold donezo-graph-chart-card__subtitle">
                          {titleInfo.pondName}
                        </div>
                        {titleInfo.dateRange && (
                          <div className="donezo-graph-chart-card__meta mt-1">{titleInfo.dateRange}</div>
                        )}
                        {titleInfo.stockedSubtitle && (
                          <div
                            className="small fw-medium mt-1"
                            style={{ color: SAMPLING_THEME.primary }}
                          >
                            <FormatWrText text={titleInfo.stockedSubtitle} />
                          </div>
                        )}
                        {titleInfo.subtitle && (
                          <div className="small donezo-graph-chart-card__meta mt-1">
                            <FormatWrText text={titleInfo.subtitle} />
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                {selectedGraphType === "population-pct-cumulative-vs-bin" &&
                  sessionList.length > 0 && (
                  <div className="mb-4 d-flex justify-content-end">
                    <div style={{ width: "100%", maxWidth: 220 }}>
                      <label
                        className="d-block small fw-semibold text-uppercase mb-1 text-end"
                        style={{ color: "var(--wf-text-muted, #64748b)", letterSpacing: "0.04em", fontSize: 11 }}
                      >
                        Sampling session
                      </label>
                      <Select
                        options={focusSessionOptions}
                        value={
                          focusSessionOptions.find(
                            (o) => Number(o.value) === Number(focusDetailId)
                          ) || null
                        }
                        onChange={(o) => {
                          if (!o?.value) {
                            sessionLoadGenRef.current += 1;
                            setIsSessionEnrichLoading(false);
                            setFocusDetailId(null);
                            setPondDetails([]);
                            return;
                          }
                          loadSessionForPopulationBinChart(o.value);
                        }}
                        isDisabled={isLoading || isSessionEnrichLoading}
                        isLoading={isSessionEnrichLoading}
                        placeholder="Select a sampling session…"
                        styles={filterSelectStyles}
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>
                )}
                <SamplingGraphExportProvider exportRef={plotExportRef}>
                  <div className="w-full p-4 md:p-6" ref={graphRef}>
                    {selectedGraphType === "population-pct-cumulative-vs-bin" &&
                      isSessionEnrichLoading && (
                      <div className="text-center py-5 text-muted">
                        <Loader2
                          size={28}
                          className="mb-2"
                          style={{ animation: "spin 0.8s linear infinite" }}
                        />
                        <div>Loading session data…</div>
                      </div>
                    )}
                    {selectedGraphType === "population-pct-cumulative-vs-bin" &&
                      !isSessionEnrichLoading &&
                      !focusDetail &&
                      sessionList.length > 0 && (
                      <div className="text-center py-5 text-muted">
                        Select a sampling session to load this graph.
                      </div>
                    )}
                    {!(
                      selectedGraphType === "population-pct-cumulative-vs-bin" &&
                      (isSessionEnrichLoading || !focusDetail)
                    ) && renderGraph()}
                    {selectedGraphType === "psd-vs-date" && (
                      <PsdTableSupplement
                        pondDetails={pondDetails}
                        selectedFish={selectedFish}
                        selectedUnit={selectedUnit}
                        precomputedRows={psdVsDateRows}
                      />
                    )}
                    {selectedGraphType === "population-pct-cumulative-vs-bin" &&
                      focusDetail &&
                      !isSessionEnrichLoading && (
                      <BinTableSupplement
                        focusDetail={focusDetail}
                        selectedFish={selectedFish}
                        selectedUnit={selectedUnit}
                        selectedBinSize={selectedBinSize}
                      />
                    )}
                  </div>
                </SamplingGraphExportProvider>
                {/* Bins Table for number-frequency */}
                {selectedGraphType === "number-frequency" && false && (
                  <div className="mt-6">
                    {/* All Fish Table */}
                    <h3 className="text-base font-semibold mb-2">
                      All Fish - Bin Summary
                    </h3>
                    <BinsTable
                      binData={processBinData(graphData, {
                        unitValue: selectedUnit.value,
                        binSizeValue: selectedBinSize?.value,
                      })}
                    />
                    {/* Individual Fish Tables */}
                    {selectedFish.length > 0 &&
                      selectedFish.map((fish) => {
                        const fishData = graphData.filter(
                          (d) => d.species === fish.label
                        );
                        if (!fishData.length) return null;
                        return (
                          <div key={fish.value} className="mt-6">
                            <h4 className="text-sm font-semibold mb-1">
                              {fish.label} - Bin Summary
                            </h4>
                            <BinsTable
                              binData={processBinData(fishData, {
                                unitValue: selectedUnit.value,
                                binSizeValue: selectedBinSize?.value,
                              })}
                            />
                          </div>
                        );
                      })}
                  </div>
                )}
          </div>
        )}
      {isPdfExporting && <Preloader />}
    </div>
  );

  if (isLoading) {
    if (isSinglePondDetailMode && pondDetailId) {
      return (
        <div className="donezo-page donezo-workflow-page py-3">
          <div className="container-fluid px-3 px-md-4">
            <PondDetailWorkflowPageHeader
              pondDetailId={pondDetailId}
              section="graph"
              detailName={
                singlePondDetailRow?.Name || selectedPond?.label || ""
              }
            />
            <PondDetailWorkflowTabs id={pondDetailId} active="graph" />
            <Preloader />
          </div>
        </div>
      );
    }
    return <Preloader />;
  }

  if (isSinglePondDetailMode && pondDetailId) {
    return (
      <div className="donezo-page donezo-workflow-page py-3">
        <div className="container-fluid px-3 px-md-4">
          <PondDetailWorkflowPageHeader
            pondDetailId={pondDetailId}
            section="graph"
            detailName={
              singlePondDetailRow?.Name || selectedPond?.label || ""
            }
          />
          <PondDetailWorkflowTabs id={pondDetailId} active="graph" />
          {pageInner}
        </div>
      </div>
    );
  }

  return pageInner;
};

export default FishDataGraphView;
